import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { Agent } from "../../../../interfaces/agent.interface";
import { agentAddress, filterAgents } from "./agentSearch";

const ICONS = "/iceholidays-assets/images/icons";

interface Props {
  id?: string;
  agents: Agent[];
  loading?: boolean;
  value?: number;
  onChange?: (agentId?: number) => void;
}

export default function AgentSelect({ id, agents, loading, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = agents.find((agent) => agent.id === value);
  const visibleAgents = useMemo(() => filterAgents(agents, query), [agents, query]);

  // antd focuses the dialog itself once it is open, so the search box takes focus back.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const openDialog = () => {
    setQuery("");
    setOpen(true);
  };

  const handlePick = (agentId: number) => {
    onChange?.(agentId);
    setOpen(false);
  };

  return (
    <>
      <button id={id} type="button" className="customize-tour-form__select-trigger" aria-haspopup="dialog" onClick={openDialog}>
        <span>{selected ? selected.name : "Please Select"}</span>
        <img src={`${ICONS}/icon-ctf-chevron-down.svg`} alt="" aria-hidden="true" />
      </button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Preferred Travel Agent"
        footer={null}
        centered
        width={932}
        afterOpenChange={(visible) => visible && searchRef.current?.focus()}
        destroyOnClose
        rootClassName="customize-tour-agent-dialog"
      >
        <label className="customize-tour-agent-dialog__search">
          <img src={`${ICONS}/icon-ctf-search.svg`} alt="" aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            inputMode="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city, area"
            aria-label="Search agents by city or area"
          />
        </label>

        <div className="customize-tour-agent-dialog__list" role="listbox" aria-label="Preferred travel agent">
          {loading && <p className="customize-tour-agent-dialog__empty">Loading agents...</p>}
          {!loading && visibleAgents.length === 0 && (
            <p className="customize-tour-agent-dialog__empty">No agents found.</p>
          )}
          {visibleAgents.map((agent) => {
            const isSelected = agent.id === value;
            return (
              <button
                key={agent.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`customize-tour-agent-dialog__row${isSelected ? " customize-tour-agent-dialog__row--selected" : ""}`}
                onClick={() => handlePick(agent.id)}
              >
                <span className="customize-tour-agent-dialog__logo">
                  {agent.image && <img src={agent.image} alt="" />}
                </span>
                <span className="customize-tour-agent-dialog__info">
                  <span className="customize-tour-agent-dialog__name">{agent.name}</span>
                  <span className="customize-tour-agent-dialog__address">{agentAddress(agent)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
